import {useI18n} from 'i18n/I18nContext'
import useAddonPayment from 'organization/Billing/Payments/useAddontPayment'
import {useOrganizationBilling} from 'organization/Billing/Providers/OrganizationBillingProvider'
import {organizationApiClient} from 'organization/organizationApiClient'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import React, {useEffect, useState} from 'react'
import {useRouteOrganizationEventId} from 'utils/lib/url'

export type AddonTypes = 'small_room' | 'medium_room' | 'large_room'

export type Addon = {
  id: number
  type: string
  name: string
  description: string
  price: number
  active: boolean
  is_purchased: boolean
}

export interface EventAddonsContextProps {
  addons: Addon[]
  loading: boolean
  buyAddon: (addonType: string) => void
}

export const EventAddonsContext = React.createContext<
  EventAddonsContextProps | undefined
>(undefined)

export default function EventAddonsProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [addons, setAddons] = useState<Addon[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [initLoading, setInitLoading] = useState<boolean>(true)
  const {payPayment} = useOrganizationBilling()
  const {payment} = useAddonPayment()
  const {locale} = useI18n()

  const eventId = useRouteOrganizationEventId()

  useEffect(() => {
    setInitLoading(true)
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, locale])

  const get = async () => {
    setLoading(true)

    const q = new URLSearchParams({locale})
    await apiClient
      .get(`api/event/${eventId}/addons?${q.toString()}`)
      .then((response) => {
        setAddons(response)
      })
      .finally(() => {
        setLoading(false)
        setInitLoading(false)
      })
  }

  const buyAddon = (addonType: string) => {
    payPayment(
      payment({
        addonType: addonType,
      }),
    )
  }

  return (
    <EventAddonsContext.Provider
      value={{
        addons,
        loading,
        buyAddon,
      }}
    >
      {initLoading && <FullPageLoader />}
      {!initLoading && <>{props.children}</>}
    </EventAddonsContext.Provider>
  )
}

export function useEventAddons() {
  const context = React.useContext(EventAddonsContext)
  if (context === undefined) {
    throw new Error('useEventAddons must be used within EventAddonsProvider')
  }
  return context
}
